import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ElementRef,
  AfterViewInit,
  ViewChild,
  inject,
} from "@angular/core";
import { LoggerService } from "../../services/logger.service";
import { DataService } from "../../services/data.service";
import { faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { BehaviorSubject, debounceTime, distinctUntilChanged, startWith, Subject, Subscription, takeUntil } from "rxjs";
import Decimal from "decimal.js";
import { Clipboard } from "@angular/cdk/clipboard";
import { CommonModule } from "@angular/common";
import { FormControl, FormsModule } from "@angular/forms";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { CustomizeTableColumnsComponent } from "../customize-table-columns/customize-table-columns.component";
import { TableSearchComponent } from "../table-search/table-search.component";
import { VFIconComponent } from "../vficon/vficon.component";
import { PaginationComponent } from "../pagination/pagination.component";
import { Router, RouterModule } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatCardModule } from "@angular/material/card";
import { NgxMatTimepickerModule } from "ngx-mat-timepicker";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  ChangeEvent,
  ChangeEventPayload,
  CheckedEvent,
  Exclusion,
  ExclusionTypeConfig,
  FilterItem,
  NotifyEvent,
  Resource,
  TableCell,
  TableColumn,
  TableRow,
  TableState,
  TableStates,
} from "../../models/common-table";
import { PaginationState } from "../../models/pagination";
import { Currency } from "../../models/currency";
import { DEFAULT_PAGE_SIZE } from "../../constant/tables";
import Swal, { SweetAlertResult } from "sweetalert2";
import { TenantService } from "../../services/tenant.service";
import { LookerStudioService } from "../../services/looker-studio.service";
import { defaultSerializer, DownloadType, SortOrder } from "../../constant/common-table";
import { EMPTY_VALS, REGEX_COMMA, REGEX_QUOTE } from "../../constant/validation";
import { VCloudSmartApiService } from "../../services/v-cloud-smart-api.service";
import { EventsService } from "../../services/events.service";
import { GroupByPayload } from "../../models/vcloud-api";
import { environment } from "../../../../environments/environment";
import { URL_REGEX } from "../../constant/urls";
import { CustomDropdownComponent } from "../custom-dropdown/custom-dropdown.component";
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TooltipModule,
    CustomizeTableColumnsComponent,
    TableSearchComponent,
    VFIconComponent,
    RouterModule,
    PaginationComponent,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    NgxMatTimepickerModule,
    FontAwesomeModule,
    CustomDropdownComponent,
  ],
  selector: "app-common-table",
  templateUrl: "./common-table.component.html",
  styleUrls: ["./common-table.component.scss"],
})
export class CommonTableComponent<T extends { [key: string]: any }>
  implements OnChanges, OnInit, OnDestroy, AfterViewInit
{
  private log = inject(LoggerService);
  private vcs = inject(VCloudSmartApiService);
  private dataService = inject(DataService);
  private events = inject(EventsService);
  private clipboard = inject(Clipboard);
  private tenant = inject(TenantService);
  private looker = inject(LookerStudioService);
  private el = inject(ElementRef);
  private router = inject(Router);

  @ViewChild("selectAllCb", { static: false }) selectAllCb!: ElementRef<HTMLInputElement>;

  // table data input bound
  @Input() tableName: string = "";

  @Input() loading = false;
  @Input() currency!: Currency;
  @Input() editable: boolean = true;

  // NOTE: the input columns do not contain info about whether
  // the column is visible or not
  // this is managed inside the component and localstorage
  @Input() columns!: TableColumn[];
  @Input() rows!: TableRow[];
  @Input() customGrandTotal!: TableRow;

  @Input() showColumnSelectAccordion = true;
  @Input() showFilterAccordion = true;
  @Input() lowerCaseInput = true;

  // this should be passed in from the query parameters by the parent page ideally
  @Input() inputFilters: FilterItem[] = [];
  @Input() selectableRows = false;

  @Input() showPagination = true;
  @Input() dynamicPagination = false;
  @Input() exactRows: number | undefined = undefined;
  @Input() pageSize = DEFAULT_PAGE_SIZE;
  @Input() showSelectAllCheckbox: boolean = false;

  @Input() disableDownload: boolean = false;
  @Input() sortWithApi: boolean = false;
  @Input() downloadFileNamePrefix: string = "";
  @Input() downloadSerializer: (row: TableRow, keys: string[]) => string | null = defaultSerializer;
  @Input() downloadKeys: string[] = [];
  @Input() downloadRequestBody?: GroupByPayload;
  @Input() activePage?: number = undefined;

  // table event handler
  @Output() changeEvent: EventEmitter<ChangeEvent<T>> = new EventEmitter();
  @Output() dataFiltered: EventEmitter<TableRow[]> = new EventEmitter();
  @Output() dataSorted: EventEmitter<{ data: TableRow[]; newOrder: SortOrder; currentOrder: SortOrder }> =
    new EventEmitter();
  @Output() pageChanged: EventEmitter<PaginationState<TableRow>> = new EventEmitter();
  @Output() filtersChanged: EventEmitter<FilterItem[]> = new EventEmitter();
  @Output() selectedColumns: BehaviorSubject<Partial<TableColumn>[]> = new BehaviorSubject(<Partial<TableColumn>[]>[]);
  @Output() selectAllRowsEvent: EventEmitter<TableRow[]> = new EventEmitter();
  @Output() sortByColumn: EventEmitter<{ column: string[]; order: SortOrder }> = new EventEmitter();
  @Output() deleteRowEvent: EventEmitter<{ action: string; row: TableRow }> = new EventEmitter();
  @Output() updateRowEvent: EventEmitter<{ action: string; row: TableRow | boolean }> = new EventEmitter();

  private lastPaginationEvent: string = "";
  readonly weekDays = new Array(7);
  public emptyValues = EMPTY_VALS;
  private destroy$ = new Subject<void>();

  // class properties
  // // table
  public tableRows: TableRow[] = [];

  // // sorting
  public sortOrders: { [colName: string]: SortOrder } = {};

  // // pagination
  public currentPage: TableRow[] = [];
  public pageNumber = 0;

  // // helpers
  public keys = Object.keys;
  public isArray = Array.isArray;

  public backgroundColors = {
    warning: "rgb(255, 255, 0, 0.2)",
    success: "rgb(144, 238, 144, 0.2)",
    danger: "rgb(250, 0, 0, 0.2)",
    white: "rgb(255, 255, 255, 0.2)",
    default: "auto",
  };

  public daysOfWeek: string[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  private timeouts: number[] = [];
  public faArrowUp = faArrowUp;
  public faArrowDown = faArrowDown;
  public findColumnValueTotal: boolean = false;
  public computeMomTrend: boolean = false;
  public deletedColumnNames: string[] = [];
  public editModeRows: number[] = [];
  protected areAllRowsSelected: boolean = false;
  private observer!: MutationObserver;
  private originalRowValue: TableRow | null = null;
  private editingRowIndex: number | null = null;
  public exclusionTypes: ExclusionTypeConfig[] = [
    {
      title: "Stop Exclusions",
      type: "stop",
      index: 0,
      buttonText: "New Stop",
      tooltip: "Add new Stop exclusion",
      colorClass: "text-danger",
      btnClass: "btn-danger",
      headerClass: "stop-header",
      getFn: (data: Resource[]) => this.getStopExclusions(data),
    },
    {
      title: "Start Exclusions",
      type: "start",
      index: 1,
      buttonText: "New Start",
      tooltip: "Add new Start exclusion",
      colorClass: "text-success mt-3",
      btnClass: "btn-success",
      headerClass: "start-header",
      getFn: (data: Resource[]) => this.getStartExclusions(data),
    },
  ];

  ngAfterViewInit(): void {
    this.observer = new MutationObserver(() => {
      const headers: NodeListOf<HTMLElement> = this.el.nativeElement.querySelectorAll("th.resizable");
      if (headers.length === 0) return;
      for (let i = 0; i < headers.length; i++) {
        this.makeResizable(headers[i]);
      }
      this.observer.disconnect();
    });
    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  private makeResizable(th: HTMLElement): void {
    Object.assign(th.style, {
      position: "relative",
      overflow: "hidden",
    });

    const resizer: HTMLSpanElement = document.createElement("span");

    Object.assign(resizer.style, {
      width: "5px",
      height: "100%",
      position: "absolute",
      top: "0",
      right: "0",
      cursor: "ew-resize",
      userSelect: "none",
      zIndex: "10",
    });
    th.appendChild(resizer);

    resizer.addEventListener("mousedown", (event: MouseEvent): void => {
      event.preventDefault();
      const startX: number = event.pageX;
      const startWidth: number = th.offsetWidth;
      const onMouseMove = (e: MouseEvent): void => {
        const newWidth: number = this.calculateNewWidth(th, startWidth, startX, e.pageX);
        this.applyNewWidth(th, newWidth, startWidth);
      };

      const onMouseUp = (): void => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });
  }

  private calculateNewWidth(th: HTMLElement, startWidth: number, startX: number, currentX: number): number {
    const headerContent: HTMLElement | null = th.querySelector(".header-content");
    const minWidth: number = headerContent?.offsetWidth ?? 100;

    return Math.max(minWidth, startWidth + (currentX - startX));
  }

  private applyNewWidth(th: HTMLElement, newWidth: number, startWidth: number): void {
    th.style.width = `${newWidth}px`;
    const headerContent: HTMLElement | null = th.querySelector(".header-content");
    if (headerContent) {
      headerContent.style.width = `${newWidth}px`;
    }
    const columnIndex: number = Array.from(th.parentElement!.children).indexOf(th);
    const rows: NodeListOf<HTMLElement> = this.el.nativeElement.querySelectorAll("tbody tr");
    for (let i = 0; i < rows.length; i++) {
      const row: HTMLElement = rows[i];
      const cell: HTMLElement | undefined = row.children[columnIndex] as HTMLElement;
      if (!cell) continue;
      cell.style.width = `${newWidth}px`;
      const cellContent: HTMLElement | null = cell.querySelector("span.d-inline-block");
      if (cellContent) {
        cellContent.style.width = `${newWidth}px`;
        const delta: number = newWidth - startWidth;
        cellContent.classList.toggle("truncate", delta < -5);
      }
    }
  }

  ngOnInit(): void {
    const state = this.getTableState();

    this.log.info("found state in localstorage:", state);

    this.pageSize = state.pageSize ?? this.pageSize ?? DEFAULT_PAGE_SIZE;
    this.pageNumber = state.pageNumber ?? this.pageNumber ?? 0;

    this.publishPaginationEvent({
      pageSize: state.pageSize ?? this.pageSize ?? DEFAULT_PAGE_SIZE,
      pageNumber: state.pageNumber ?? this.pageNumber ?? 0,
    });

    if (this.inputFilters?.length === 0) {
      this.inputFilters = state?.filterItems ?? [];
      this.filtersChanged.next(state?.filterItems ?? []);
    }

    if (((state.selectedColumns || []).length ?? 0) > 0) {
      for (const { selected, title } of state.selectedColumns) {
        const column = this.columns.find((c) => c?.title === title);
        if (column && selected !== undefined) {
          column.selected = selected;
          if (!selected) {
            this.deletedColumnNames.push(column.title);
          }
        }
      }
      this.selectedColumns.next(this.columns);
    }
    this.initializeMultiSelectControls();
  }

  // Initializes FormControl for multiSelect cells if there any
  private initializeMultiSelectControls(): void {
    this.currentPage?.forEach((row: TableRow) => {
      row.forEach((cell: TableCell<any>) => {
        if (cell?.inputType === "multiSelect" && !cell.control) {
          if (!cell.originalList) {
            cell.originalList = [...(cell.list || [])] as string[];
          }
          cell.control = new FormControl(cell.selected || []);
          cell.searchControl = new FormControl("");
          cell.searchControl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), startWith(""), takeUntil(this.destroy$))
            .subscribe((searchValue: string) => {
              cell.list = this.filterMultiSelectItems(
                cell.originalList as string[],
                searchValue,
                (cell.selected as string[]) || [],
              );
            });
          cell.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedValues: string[]) => {
            cell.selected = selectedValues;
            cell.value = selectedValues;
            const currentSearch = (cell.searchControl && cell.searchControl.value) || "";
            cell.list = this.filterMultiSelectItems(cell.originalList as string[], currentSearch, selectedValues);
            this.notifyCellChange({ value: selectedValues }, cell.data, this.getColumnTitle(row, cell), row);
          });
        }
      });
    });
  }

  private getColumnTitle(row: any[], cell: any): string {
    const cellIndex: number = row.indexOf(cell);
    return this.columns[cellIndex]?.title || "";
  }

  public toggleEditMode(rowIndex: number): void {
    const wasInEditMode: boolean = this.editModeRows.includes(rowIndex);
    const row: TableRow = this.currentPage[rowIndex];
    if (wasInEditMode) {
      this.editModeRows = this.editModeRows.filter((idx) => idx !== rowIndex);
      this.originalRowValue = null;
      this.editingRowIndex = null;
    } else {
      this.editingRowIndex = rowIndex;
      this.originalRowValue = row.map(
        (cell: TableCell<any>) =>
          ({
            ...cell,
            value: this.deepCopyValue(cell.value),
            selected: Array.isArray(cell.selected) ? [...cell.selected] : cell.selected,
            list: cell.list ? [...cell.list] : cell.list,
            originalList: cell.originalList ? [...cell.originalList] : cell.originalList,
          }) as TableCell<any>,
      );
      this.log.info("Stored original value for row", rowIndex);
      this.editModeRows.push(rowIndex);
      row?.forEach((cell: TableCell<any>, cellIndex: number) => {
        if (cell?.inputType === "multiSelect" && !cell.control) {
          if (!cell.originalList) {
            cell.originalList = [...(cell.list || [])] as string[];
          }
          cell.control = new FormControl(cell.selected || []);
          cell.searchControl = new FormControl("");
          cell.searchControl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), startWith(""), takeUntil(this.destroy$))
            .subscribe((searchValue: string) => {
              cell.list = this.filterMultiSelectItems(
                cell.originalList as string[],
                searchValue,
                (cell.selected as string[]) || [],
              );
            });
          cell.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedValues: string[]) => {
            cell.selected = selectedValues;
            cell.value = selectedValues;
            const currentSearch: string = (cell.searchControl && cell.searchControl.value) || "";
            cell.list = this.filterMultiSelectItems(cell.originalList as string[], currentSearch, selectedValues);
            const column: TableColumn = this.columns[cellIndex];
            if (column) {
              this.notifyCellChange(selectedValues, cell.data, column.title, row);
            }
          });
        }
      });
    }
  }

  private filterMultiSelectItems(items: string[], searchValue: string, selectedValues: string[]): any[] {
    if (!items || items.length === 0) {
      return [];
    }
    const lowerCaseSearchValue: string = searchValue?.toLowerCase() || "";
    const filteredItems: string[] = items.filter((item: any) => {
      if (!lowerCaseSearchValue) return true;
      if (typeof item === "string") {
        return item.toLowerCase().includes(lowerCaseSearchValue);
      }
      const displayValue: string = item.display_name || item.name || item.id || "";
      return displayValue.toLowerCase().includes(lowerCaseSearchValue);
    });
    const selectedItems: string[] = items.filter((item: any) => {
      if (typeof item === "string") {
        return selectedValues.includes(item);
      }
      const itemId: string = item.id || "";
      const itemDisplayName: string = item.display_name || item.name || "";
      return selectedValues.includes(itemId) || selectedValues.includes(itemDisplayName);
    });

    return [...new Set([...filteredItems, ...selectedItems])];
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.log.info("common table changes", changes);
    if (changes.columns) {
      const state: TableState = this.getTableState();
      let selectedColumns = state.selectedColumns || [];
      if (selectedColumns.length === 0) {
        selectedColumns = changes.columns.currentValue;
      }
      selectedColumns = selectedColumns.map(({ selected, ...c }) => ({
        ...c,
        selected: selected === undefined ? true : selected,
      }));
      for (const { title, selected } of selectedColumns) {
        const column = this.columns.find((c) => c.title === title);
        if (column) {
          column.selected = selected;
        }
      }
      for (const c of this.columns) {
        if (c.sum) this.findColumnValueTotal = true;
        if (c.computeMomTrend) this.computeMomTrend = true;
      }
    }

    if (changes?.inputFilters || changes?.rows) {
      this.log.info("detected changes to data or filters");
      if ((changes?.rows?.currentValue?.length ?? 0) < (changes?.rows?.previousValue?.length ?? 0)) {
        this.pageNumber = 0;
        this.setTableStates({ pageNumber: this.pageNumber });
      }
      this.tableRows = this.getFilteredData(this.rows, this.columns, this.inputFilters);
      this.areAllRowsSelected = this.checkIfAllRowsSelected(this.tableRows);
      if (changes?.rows && !changes?.rows?.firstChange) {
        const startIndex: number = this.pageNumber * this.pageSize;
        const endIndex: number = startIndex + this.pageSize;
        this.currentPage = this.tableRows.slice(startIndex, endIndex);
      }

      if (changes.inputFilters && !changes.inputFilters.firstChange) {
        this.setTableStates({ filterItems: this.inputFilters });
      }

      // Re-initialize controls for rows that are in edit mode after rows change
      if (changes?.rows && this.editModeRows.length > 0) {
        this.log.info("Re-initializing controls for edit mode rows after row changes");
        setTimeout(() => {
          this.editModeRows.forEach((rowIndex) => {
            const row: TableRow = this.currentPage[rowIndex];
            if (row) {
              row.forEach((cell: any, cellIndex: number) => {
                if (cell?.inputType === "multiSelect" && !cell.control) {
                  if (!cell.originalList) {
                    cell.originalList = [...(cell.list || [])];
                  }
                  cell.control = new FormControl(cell.selected || []);
                  cell.searchControl = new FormControl("");
                  cell.searchControl.valueChanges
                    .pipe(debounceTime(300), distinctUntilChanged(), startWith(""), takeUntil(this.destroy$))
                    .subscribe((searchValue: string) => {
                      cell.list = this.filterMultiSelectItems(cell.originalList, searchValue, cell.selected || []);
                    });
                  cell.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedValues: string[]) => {
                    cell.selected = selectedValues;
                    cell.value = selectedValues;
                    const currentSearch: string = cell.searchControl.value || "";
                    cell.list = this.filterMultiSelectItems(cell.originalList, currentSearch, selectedValues);
                    // Trigger change event to notify parent component
                    const column: TableColumn = this.columns[cellIndex];
                    if (column) {
                      this.notifyCellChange(selectedValues, cell.data, column.title, row);
                    }
                  });
                }
              });
            }
          });
        }, 0);
      }
    }

    if (this.activePage) {
      this.setTableStates({ pageNumber: this.activePage });
      this.pageNumber = this.activePage;
    }
  }

  private checkIfAllRowsSelected(rows: TableRow[]): boolean {
    return !rows.some((r) => {
      return r?.find((cell) => cell?.data)?.data?.["$Selected"] !== true;
    });
  }

  private deepCopyValue(value: any): any {
    if (!Array.isArray(value)) {
      return value;
    }
    return value.map((item) => {
      if (Array.isArray(item)) {
        return [...item];
      }
      return item;
    });
  }

  private publishPaginationEvent(event: PaginationState<TableRow>): void {
    if (this.lastPaginationEvent !== "" && this.lastPaginationEvent === JSON.stringify(event)) {
      return;
    }
    this.lastPaginationEvent = JSON.stringify(event);
    this.pageChanged.next(event);
  }

  private get isDownloadAsync(): boolean {
    return this.downloadRequestBody !== undefined && this.downloadRequestBody !== null;
  }
  public uncheckSelectAll(): void {
    if (this.selectAllCb) {
      this.selectAllCb.nativeElement.checked = false;
      this.areAllRowsSelected = false;
    }
  }

  public extractUrl(tooltip?: string): string | undefined {
    const match = URL_REGEX.exec(tooltip || "");
    if (match && match.length > 0 && match[0].length > 0) {
      return match[0];
    }
  }

  public handleColumnTooltipClick(tooltip?: string): void {
    this.log.debug("handling click on tooltip:", tooltip);
    const url = this.extractUrl(tooltip);
    if (url) {
      this.log.info("opening:", url);
      window.open(url, "_blank");
    }
  }

  public trackByColumn(index: number, colum: TableColumn): string {
    return colum.label || colum.title;
  }

  public notifyCellChange(
    event: unknown,
    data: T,
    column: string,
    row: TableRow,
    options: { [key: string]: any } | null = null,
    action?: string,
  ): void {
    const columnIdx: number = this.columns.findIndex((c) => c.title === column);
    const cell: TableCell<any> = row[columnIdx];
    if (!cell) return;
    const newValue: string = cell.control ? cell.control.value : cell.value;
    this.log.info({ msg: "Cell change detected", column, newValue });
    if (cell.inputType === "multiSelect" && this.isRowInEditMode(this.currentPage.indexOf(row))) {
      cell.selected = newValue;
      return;
    }
    if (cell.type === "scheduler" && cell.subtype === "time" && Array.isArray(cell.data) && Array.isArray(cell.value)) {
      const scheduleIndex: number = cell.data.findIndex((schedule: any) => schedule === data);
      if (scheduleIndex !== -1 && typeof event === "string") {
        cell.value[scheduleIndex] = event;
        this.log.info({ msg: "Time cell updated", scheduleIndex, newTime: event });
      }
    }
    this.areAllRowsSelected = this.checkIfAllRowsSelected(this.tableRows);
    let processedData: T | any = data;
    if (action && Array.isArray(data)) {
      const matchedItem: { action?: string; enabled?: boolean | string } | undefined = (
        data as Array<{ action?: string; enabled?: boolean | string }>
      ).find((item) => item?.action?.toLowerCase().includes(action.toLowerCase()));
      if (matchedItem) {
        matchedItem.enabled = "now";
        processedData = matchedItem;
      }
    }
    this.changeEvent.next({
      event,
      data: processedData,
      column,
      options,
      row,
      action,
    } as ChangeEventPayload<T>);
  }

  // customize table columns handler
  public handleColumnSelection(updatedColumns: Partial<TableColumn>[]): void {
    this.log.info("handling column change:", updatedColumns);
    this.deletedColumnNames = [];
    for (const column of updatedColumns) {
      const c = this.columns.find((c) => c.title === column.title);
      if (c && c.selected !== column.selected) {
        this.log.info("column ", c?.title, c?.label, column.selected ? "selected" : "de-selected");
        c.selected = column.selected;
      }
      if (!column.selected) {
        this.deletedColumnNames.push(column.title as string);
      }
    }
    this.setTableStates({ selectedColumns: this.columns });
    this.selectedColumns.next(this.columns);
  }

  public handlePaginationChange(state: PaginationState<TableRow>): void {
    // Check if anything ACTUALLY changed to avoid redundant processing
    if (
      state.pageNumber === this.pageNumber &&
      state.pageSize === this.pageSize &&
      this.currentPage.length === state.currentPage?.length
    ) {
      return;
    }
    this.log.info("handling pagination change:", state);
    const { pageNumber, pageSize, currentPage } = state;
    this.pageSize = pageSize;
    this.pageNumber = pageNumber;
    this.currentPage = currentPage as TableRow[];
    this.areAllRowsSelected = this.checkIfAllRowsSelected(this.tableRows);
    setTimeout(() => this.setTableStates({ pageNumber, pageSize }));
    this.publishPaginationEvent(state);
  }
  public getStopExclusions(resources: Resource[]): Exclusion[] {
    return resources?.[0]?.exclude ?? [];
  }

  public getStartExclusions(resources: Resource[]): Exclusion[] {
    return resources?.[1]?.exclude ?? [];
  }

  public handleExclusionAdd(resources: Resource[], type: "start" | "stop"): void {
    const targetIndex = type === "stop" ? 0 : 1;
    const resource: Resource = resources[targetIndex];
    if (!resource.exclude) {
      resource.exclude = [];
    }
    resource.exclude.push({ key: "", value: "" });
  }
  private getRowSorter(columnIdx: number, order: SortOrder = "ASC") {
    return (r1: TableRow, r2: TableRow) => {
      let v1 = (r1[columnIdx] || {}).rawValue ?? (r1[columnIdx] || {}).selected ?? (r1[columnIdx] || {}).value ?? 0;
      let v2 = (r2[columnIdx] || {}).rawValue ?? (r2[columnIdx] || {}).selected ?? (r2[columnIdx] || {}).value ?? 0;
      if (order === "DESC") {
        // swap values
        const tmp = v2;
        v2 = v1;
        v1 = tmp;
      }
      if (!v1 || EMPTY_VALS.includes(v1)) {
        return -1;
      } else if (!v2 || EMPTY_VALS.includes(v2)) {
        return 1;
      } else if (v1 < v2) {
        return -1;
      } else if (v1 === v2) {
        return 0;
      } else {
        return 1;
      }
    };
  }

  public sortDataByColumn(column: TableColumn, columnIdx: number): void {
    this.log.info("sort on ", column, columnIdx);
    const currentOrder: SortOrder = this.sortOrders[column?.title] || "ASC";
    const newOrder: SortOrder = currentOrder === "ASC" ? "DESC" : "ASC";

    if (this.sortWithApi) {
      this.sortByColumn.emit({ column: [column.title], order: newOrder });
      this.sortOrders[column.title] = newOrder;
    } else {
      const hasGrandTotal: boolean =
        this.customGrandTotal !== undefined ||
        this.tableRows[this.tableRows.length - 1]?.some((cell) => cell?.data?.isTotal);
      if (this.tableRows.length <= 1 || !hasGrandTotal) {
        this.tableRows = [...this.tableRows.sort(this.getRowSorter(columnIdx, newOrder))];
      } else {
        this.tableRows = [
          ...this.tableRows.slice(0, -1).sort(this.getRowSorter(columnIdx, newOrder)),
          ...this.tableRows.slice(-1),
        ];
      }
      const startIndex: number = this.pageNumber * this.pageSize;
      const endIndex: number = startIndex + this.pageSize;
      this.currentPage = this.tableRows.slice(startIndex, endIndex);
      this.sortOrders[column.title] = newOrder;
      this.dataSorted.next({
        currentOrder,
        newOrder,
        data: this.tableRows,
      });
    }
  }

  private getFilteredData(rows: TableRow[], columns: TableColumn[], filterItems: Array<FilterItem>): Array<TableRow> {
    this.log.info("filtering data with:", filterItems);
    let foundData: TableRow[];
    if (filterItems.length === 0) {
      foundData = [...rows];
    } else {
      foundData = rows.filter((row) => {
        return !filterItems.some((filterItem) => {
          const columnIdx = columns.findIndex((column) => {
            return (column.label || column.title) === (filterItem.columnLabel || filterItem.columnTitle);
          });
          let cells: TableCell<any>[];

          if ((filterItem.columnLabel || filterItem.columnTitle).toUpperCase() === "ALL") {
            cells = row;
          } else if (columnIdx >= 0) {
            cells = [row[columnIdx]];
          } else {
            cells = [];
          }

          return !cells.some((cell) =>
            filterItem.searchText
              .map((s) => s.toLowerCase())
              .some((filterText) => {
                if (cell) {
                  const value = cell.selected || cell.value;
                  if (value) {
                    const s = value.toString().toLowerCase();
                    if (s) {
                      if (filterText.startsWith("=")) {
                        filterText = filterText.slice(1);
                        return s === filterText;
                      }
                      return s.includes(filterText);
                    }
                  }
                }
                return false;
              }),
          );
        });
      });
    }
    this.log.info("filtered data", foundData);
    this.dataFiltered.next(foundData);
    this.pageNumber = 0;
    this.setTableStates({ pageNumber: this.pageNumber });
    return foundData;
  }

  public getTableState(): TableState {
    let result: TableState = {
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      filterItems: [],
      selectedColumns: [],
    };
    // this would clash with auto-selecting of all unverified rows so we are disabling it for csp table
    if (this.tableName === "csp-table") {
      return result;
    }
    try {
      const url: string = this.getUrl();
      this.log.info("getting table states, url: ", url, "table name: ", this.tableName ?? "");
      const currentState: TableState = JSON.parse(localStorage.getItem("tableStates") ?? "{}")?.[url]?.[
        this.tableName ?? ""
      ];
      if (currentState !== undefined) {
        this.log.warning("retrieved table states");
        result = Object.assign(result, currentState);
      }
    } catch (e) {
      this.log.error(e);
      localStorage.setItem("tableStates", "{}");
    }
    return result;
  }

  public setTableStates(updates: Partial<TableState>): void {
    if ((this.pageSize ?? 0) > 0) {
      const totalPages = Math.ceil((this.rows?.length ?? 0) / this.pageSize);
      if ((this.pageNumber ?? 0) >= totalPages) {
        this.pageNumber = Math.max(0, totalPages - 1);
      }
    }
    if (this.tableName === "csp-table") {
      return;
    }
    try {
      const url: string = this.getUrl();

      this.log.info("setting table states for:", url, "to be:", updates, " table name: ", this.tableName ?? "");

      let tableStates: TableStates = JSON.parse(localStorage.getItem("tableStates") ?? "{}") ?? {};

      // if either filter or page filter doesn't exist
      if (!tableStates) {
        tableStates = {};
      }

      if (!tableStates[url]) {
        tableStates[url] = {};
      }

      if (!tableStates[url][this.tableName ?? ""]) {
        tableStates[url][this.tableName ?? ""] = {
          pageSize: this.pageSize,
          pageNumber: 0,
          filterItems: [],
          selectedColumns: this.selectedColumns?.value ?? [],
        };
      }

      const dstObj: Partial<TableState> = tableStates?.[url]?.[this.tableName] ?? {};
      Object.assign(dstObj, updates);
      localStorage.setItem("tableStates", JSON.stringify(tableStates));
    } catch (e) {
      this.log.error(e);
      localStorage.setItem("tableStates", "{}");
    }
  }

  private getUrl(): string {
    const url = `${location.pathname}${location.hash}`;
    if (url === "/dashboard#services" || url === "/dashboard#tenancies") {
      return "/dashboard#change-approvals";
    } else {
      return url;
    }
  }

  public handleFiltersChange(filterItems: FilterItem[]) {
    this.log.info("new filters", filterItems);
    this.inputFilters = filterItems;
    this.tableRows = this.getFilteredData(this.rows, this.columns, this.inputFilters);
    const startIndex: number = this.pageNumber * this.pageSize;
    const endIndex: number = startIndex + this.pageSize;
    this.currentPage = this.tableRows.slice(startIndex, endIndex);
    this.filtersChanged.next(filterItems);
  }

  public columnTotal(index: number): string {
    let sum: number = 0;
    for (const r of this.tableRows || []) {
      sum += Number(r[index].rawValue) || 0;
    }
    return this.currency ? this.currency.format.format(sum) : this.looker.formatting.formatNumber(sum, 2).fmt;
  }

  public columnMomTrend(index: number): string {
    let currentSum = new Decimal(0);
    let compareSum = new Decimal(0);

    for (const r of this.tableRows) {
      const val = r[index].data.Value[0];
      const val2 = r[index].data.Value[1];
      if (val !== 0) {
        currentSum = currentSum.add(val);
      }
      if (val2 !== 0) {
        compareSum = compareSum.add(val2);
      }
    }

    let momTrend = compareSum.minus(currentSum).dividedBy(currentSum).mul(100);
    return new Intl.NumberFormat().format(momTrend.toDecimalPlaces(2).toNumber());
  }

  public async download(type: DownloadType): Promise<void> {
    if (this.tableName.startsWith("llm-table-")) {
      if (type === "all" && environment?.featureToggles?.asyncExport) {
        return this.downloadAsync();
      }
      // sync export, only visible (loaded) data is being downloaded here
      else {
        return await this.downloadSync(type);
      }
    } else {
      if ((environment?.featureToggles?.asyncExport ?? false) && this.isDownloadAsync && type === "all") {
        return this.downloadAsync();
      } else {
        return await this.downloadSync(type);
      }
    }
  }

  private subscriptions: Subscription[] = [];

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.timeouts.forEach((s) => clearTimeout(s));
  }

  public downloadAsync(): void {
    this.subscriptions.push(
      this.vcs.groupByAsync(this.downloadRequestBody as GroupByPayload, this.currency, this.columns).subscribe(
        () => {
          Swal.fire({
            title: "<strong>Export Initiated</strong>",
            icon: "success",
            html: `<strong>NOTE:</strong> On clicking <b>Go To Export Page</b>, you will be redirected to the Data Export page where you can download the report.<br><br>
              The export time depends on the amount of time you have access to, the filters applied, and the report you are on.<br><br>`,
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: '<i class="fa fa-download"></i> Go To Export Page',
            cancelButtonText: '<i class="fa fa-check"></i> Close',
          }).then((result) => {
            if (result.isConfirmed) {
              const match = window.location.href.match(/\/(aws|gcp|azure|drcc|oci)\//);
              const redirectUrl = match ? `/dashboard#vcloudsmart/${match[1]}` : `/dashboard#vcloudsmart`;
              this.router.navigateByUrl(redirectUrl);
            }
          });
        },
        (e) => {
          this.events.displayErrorToUser(e, "Export data from table");
        },
      ),
    );
  }

  public async downloadSync(type: DownloadType): Promise<void> {
    let columns: TableColumn[] = [...this.columns];

    this.log.info({
      msg: "initiating synchronous download",
      type,
      columns,
      selectableRows: this.selectableRows,
    });

    if (this.selectableRows) {
      columns = columns.slice(1);
    }

    let deselectedColumns: TableColumn[] = columns.filter((c) => !c.selected);
    const columnSelectionUsed: boolean = (deselectedColumns?.length ?? 0) > 0;

    this.log.info({ columnSelectionUsed, deselectedColumns, columns });

    let userConsent: SweetAlertResult<Awaited<unknown>>;
    let onlyDownloadVisibleColumns: boolean = false;

    if (columnSelectionUsed) {
      userConsent = await Swal.fire({
        title: "Only Visible Columns?",
        text: "Do you want to download only visible columns?",
        confirmButtonColor: this.tenant.colors.primary,
        cancelButtonColor: this.tenant.colors.secondary,
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        backdrop: false,
      });
      onlyDownloadVisibleColumns = userConsent?.isConfirmed ?? false;
      if (onlyDownloadVisibleColumns) {
        columns = columns.filter((c) => c.selected);
      }
    }

    let rowKeys: string[];
    let headers: string[];

    if ((this.downloadKeys?.length ?? 0) === 0) {
      rowKeys = columns.map((c) => c.title);
      headers = columns.map((c) => c.label) as string[];
    } else {
      rowKeys = this.downloadKeys;
      headers = this.downloadKeys;
    }

    const headings: string = headers.map((s) => s.replace(REGEX_QUOTE, "").replace(REGEX_COMMA, "")).join(",");
    let rowsToSave: TableRow[];

    if (type === "filtered") {
      rowsToSave = this.tableRows;
    } else if (type === "selected") {
      rowsToSave = this.rows.filter((row) => row?.[0]?.value === true);
    } else {
      rowsToSave = this.rows;
    }

    if (onlyDownloadVisibleColumns) {
      rowsToSave = rowsToSave.map((r) => r.filter((_, cIdx) => this.columns?.[cIdx]?.selected === true));
    }

    if (this.selectableRows) {
      rowsToSave = rowsToSave.map((r) => r.slice(1));
    }

    this.log.info(`Saving ${type} rows (count: ${rowsToSave.length}/${this.rows.length})`);

    this.log.debug({ headings, headers, rowKeys, rowsToSave });

    const csvRows: Array<string> = [headings];

    for (const row of rowsToSave) {
      const serialisedRow: string = this.downloadSerializer(row, rowKeys) as string;
      if (serialisedRow !== null) {
        csvRows.push(serialisedRow);
      }
    }

    this.dataService.download(csvRows.join("\n"), this.getDownloadFileName());
  }

  private getDownloadFileName(): string {
    let prefix: string = this.tableName;

    if (this.downloadFileNamePrefix != "") {
      prefix = this.downloadFileNamePrefix;
    }

    return `${prefix}-${new Date().toISOString()}.csv`;
  }

  public copyValue(value: string): void {
    if (value) {
      if (typeof value === "string" && value.startsWith("mailto:")) {
        value = value.replace("mailto:", "");
      }
      this.clipboard.copy(value.toString());
    }
  }

  public columnNotExcluded(column: TableColumn | TableCell<T>, index: number): boolean {
    if (this.deletedColumnNames.length === 0) {
      return true;
    }
    if (!column.title) {
      column.title = this.columns[index].title;
    }
    return !this.deletedColumnNames.includes(column.title);
  }

  public selectAllRows(event: Event, rows: TableRow[]): void {
    this.log.info("selecting all rows", event, rows);
    let newVal: boolean;
    let rowsToEmit: TableRow[];
    this.areAllRowsSelected = this.checkIfAllRowsSelected(rows);
    if (this.areAllRowsSelected) {
      newVal = false;
      rowsToEmit = [];
    } else {
      newVal = true;
      rowsToEmit = rows;
    }
    for (const r of rows) {
      const d = r.find((cell) => cell.data)?.data;
      if (d) {
        d["$Selected"] = newVal;
      }
    }
    this.areAllRowsSelected = newVal;
    this.selectAllRowsEvent.next(rowsToEmit);
  }

  public getStateClass(state: string): string {
    if (!state) return "state-unknown";
    const normalizedState: string = state.toLowerCase().replace(/_/g, "-");
    if (normalizedState === "unknown-enum-value") {
      return "state-unknown";
    }
    return `state-${normalizedState}`;
  }

  public getButtonConfig(state: string) {
    const normalizedState = state?.toUpperCase();
    switch (normalizedState) {
      case "RUNNING":
        return {
          text: "Stop",
          cssClass: "action-btn btn-stop",
          disabled: false,
        };
      case "STOPPED":
        return {
          text: "Start",
          cssClass: "action-btn btn-start",
          disabled: false,
        };
      default:
        return {
          text: "Start",
          cssClass: "action-btn btn-disabled",
          disabled: true,
        };
    }
  }

  public deleteRow(row: TableRow): void {
    this.updateRowEvent.emit({ action: "delete", row: row });
  }

  public saveRowChanges(row: TableRow, rowIndex: number): void {
    this.originalRowValue = null;
    this.editingRowIndex = null;
    this.toggleEditMode(rowIndex);
    this.updateRowEvent.emit({ action: "update", row: row });
  }

  public isRowInEditMode(rowIndex: number): boolean {
    return this.editModeRows.includes(rowIndex);
  }

  public cancelRowEdit(rowIndex: number): void {
    if (this.originalRowValue && this.editingRowIndex === rowIndex) {
      this.currentPage[rowIndex] = this.originalRowValue.map(
        (cell: TableCell<any>) =>
          ({
            ...cell,
            value: this.deepCopyValue(cell.value),
            selected: Array.isArray(cell.selected) ? [...cell.selected] : cell.selected,
            list: cell.list ? [...cell.list] : cell.list,
            originalList: cell.originalList ? [...cell.originalList] : cell.originalList,
          }) as TableCell<any>,
      );
      this.originalRowValue = null;
      this.editingRowIndex = null;
    }

    this.toggleEditMode(rowIndex);
  }

  public isGroupSchedulerTable(): boolean {
    return this.tableName === "group-scheduler-table";
  }
}
